import logo from '@/assets/logos/ava-logo.png'
import { useRef } from 'react'
import { router } from '@inertiajs/react'
import { Dropdown } from '@/components/ui'
import type { MenuProps } from '@/components/ui'
import { Icon } from '../icons'
import { csrfToken } from '@/lib/csrf'

export interface LoggedInUser {
  name?: string
  role?: string
  email?: string
}


interface HeaderProps {
  loggedInUser?: LoggedInUser
}

// Logo Component
function HeaderLogo() {
  return (
    <div className="flex items-center space-x-3">
      <div className="flex items-center">
        <img src={logo} alt="AVA Logo" className="h-10 object-contain" />
      </div>
    </div>
  )
}

// User Info Component
interface UserInfoProps {
  name?: string
  role?: string
}

function UserInfo({ name, role }: UserInfoProps) {
  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm text-ava-black font-bold">{name}</span>
      <span className="text-sm text-ava-black font-bold">/</span>
      <span className="text-sm text-ava-black">{role}</span>
    </div>
  )
}

// User Avatar Button Component
interface UserAvatarProps {
  name?: string
}

function UserAvatar({ name }: UserAvatarProps) {
  // Get initials from name (up to 2 characters)
  const getInitials = (fullName?: string) => {
    if (!fullName) return ''
    const names = fullName.trim().split(' ').filter(Boolean)
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase()
    }
    return names[0]?.charAt(0).toUpperCase() || ''
  }

  const initials = getInitials(name)

  return (
    <button
      type="button"
      className="w-8 h-8 rounded-full bg-ava-black border border-white flex items-center justify-center text-white hover:opacity-90 focus:outline-none text-xs font-medium"
      aria-haspopup="menu"
      aria-label="User menu"
    >
      {initials || <Icon name="user" size={18} />}
    </button>
  )
}

// Logout Form Component
interface LogoutFormProps {
  formRef: React.RefObject<HTMLFormElement>
}

function LogoutForm({ formRef }: LogoutFormProps) {
  // Rendered value is only a placeholder; submitLogoutForm refreshes it at
  // submit time so a session change after render cannot strand the token.
  return (
    <form ref={formRef} method="post" action="/logout" className="hidden">
      <input type="hidden" name="authenticity_token" value={csrfToken() || ''} />
      <input type="hidden" name="_method" value="delete" />
    </form>
  )
}

// User Menu Component
interface UserMenuProps {
  loggedInUser: LoggedInUser
  email?: string
  onLogout: () => void
}

function UserMenu({ loggedInUser, email, onLogout }: UserMenuProps) {
  // Get initials from name (up to 2 characters)
  const getInitials = (fullName?: string) => {
    if (!fullName) return ''
    const names = fullName.trim().split(' ').filter(Boolean)
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase()
    }
    return names[0]?.charAt(0).toUpperCase() || ''
  }

  const initials = getInitials(loggedInUser.name)

  const menuItems: MenuProps['items'] = [
    {
      key: 'change-password',
      label: (
        <div className="py-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-ava-black border border-white flex items-center justify-center text-white text-sm font-medium">
              {initials || <Icon name="user" size={20} />}
            </div>
            <div className="flex flex-col flex-1">
              <span className="text-sm font-semibold text-black">{loggedInUser.name}</span>
              {email && <span className="text-xs text-gray-500">{email}</span>}
            </div>
          </div>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'change-password',
      label: <span className="text-sm">Change Password</span>,
    },
    {
      key: 'logout',
      label: <span className="text-sm">Log Out</span>,
    },
  ]

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key }) => {
          if (key === 'change-password') {
            window.location.href = '/account/password'
          } else if (key === 'logout') {
            onLogout()
          }
        },
      }}
      trigger={['click']}
      placement="bottomRight"
    >
      <div className="flex items-center space-x-3 cursor-pointer">
        <UserInfo name={loggedInUser.name} role={loggedInUser.role} />
        <UserAvatar name={loggedInUser.name} />
      </div>
    </Dropdown>
  )
}

// Main Header Component
export default function Header({ loggedInUser }: HeaderProps) {
  const logoutFormRef = useRef<HTMLFormElement | null>(null)

  const handleLogout = () => {
    const form = logoutFormRef.current
    if (!form) return
    const tokenInput = form.querySelector<HTMLInputElement>('input[name="authenticity_token"]')
    if (tokenInput) tokenInput.value = csrfToken() || ''
    form.submit()
  }

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-20 shadow-sm">
      <div className="flex items-center justify-between">
        <HeaderLogo />
        {loggedInUser && (
          <UserMenu loggedInUser={loggedInUser} email={loggedInUser.email} onLogout={handleLogout} />
        )}
        <LogoutForm formRef={logoutFormRef} />
      </div>
    </header>
  )
}
